import {useServices} from '@/services/context'
import {useQuery} from '@tanstack/react-query'
import {AttributeOperation} from '@/types/attribute-operation'
import {AttributeEntityType} from '@/types/attribute-entity-type'
import type {AttributeDefinition} from '@/types/attribute-definition'

const ENTITY_TYPE_API_PATH: Record<'PARTY' | 'IDENTITY', AttributeEntityType> = {
    PARTY: AttributeEntityType.PARTY,
    IDENTITY: AttributeEntityType.IDENTITY,
}

/**
 * Fetches attribute definitions for a given entity type and type code.
 * Returns only IAM-managed definitions (operations includes CREATE).
 */
export function useAttributeDefinitions(entityType: 'PARTY' | 'IDENTITY', typeCode: string) {
    const apiPath = ENTITY_TYPE_API_PATH[entityType]

    const services = useServices()
    const {data, isLoading, error} = useQuery({
        queryKey: ['attribute-definitions', entityType, typeCode],
        // Pass the raw typeCode — buildQueryString in the transport layer encodes it.
        // Encoding here would double-encode (`/` → `%2F` → `%252F`) and the backend
        // would query for the wrong type code; standard ASCII codes are no-ops which
        // is why this lay latent until a custom type with spaces or slashes appeared.
        queryFn: () => services.attributeDefinitions.list(apiPath, {typeCode}),
        enabled: !!typeCode,
    })

    // Filter to IAM-managed definitions only (operations includes CREATE)
    const definitions = (data ?? []).filter(
        (def: AttributeDefinition) => def.active && def.operations.includes(AttributeOperation.CREATE)
    )

    return {definitions, isLoading, error}
}
